import calendarGraphqlRequest from "../calendarGraphqlRequest";
import {
  GQL_ASSESSMENT_CALENDAR,
  GQL_GET_CURRENT_PERIOD,
  GQL_GET_CURRENT_SCHOOL_YEAR_CALENDAR,
  GQL_GET_PERIOD,
  GQL_GET_PERIOD_BY_ID,
  GQL_GET_PERIOD_FOR_DATE,
  GQL_PING,
  GQL_SCHOOL_YEAR_CALENDAR,
  GQL_UPSERT_SCHOOL_YEAR_CALENDAR,
} from "./calendarQueries";

// --- Types (Copied from calendarTypes.ts) ---

/**
 * Represents a date in 'yyyy-MM-dd' format.
 */
export type DateOnly = string;

/**
 * Represents a potentially large integer.
 */
export type Long = number;

/**
 * The type of academic break period: Holiday or Break.
 */
export enum BreakType {
	BREAK = "BREAK",
	NONINSTRUCTIONAL = "NONINSTRUCTIONAL",
	HOLIDAY = "HOLIDAY",
}

export type AcademicBreakPeriod = {
	/** Name of the break period. */
	name: string;
	/** Type of the break period. */
	breakType: BreakType;
	startDate?: DateOnly;
	endDate?: DateOnly;
};

export type SchoolYearCalendar = {
	/** The domain ID associated with the calendar. */
	districtId?: string;
	/** The school ID associated with the calendar. */
	schoolId?: string;
	/** The school year. */
	schoolYear: number;
	/** The name of the school year calendar. */
	name: string;
	/** The start date of the school year. */
	startDate?: DateOnly;
	/** The end date of the school year. */
	endDate?: DateOnly;
	/** The break periods. */
	academicBreakPeriods?: AcademicBreakPeriod[];
};

/** A period with an identifier. */
export type IdentifiedPeriod = {
  /** name of this period. */
  name: string;
  /** Id of this period. */
  periodIdentifier: number;
  /** The start date of the period. */
  startDate: DateOnly;
  /** The end date of the period. */
  endDate: DateOnly;
  /** The tags associated with this period. */
  tags: string[];
  /** The school year. */
  schoolYear: number;
}

/** The assessment calendar for an academic year. */
export type AssessmentCalendar = {
  /** The domain ID associated with the calendar. */
  districtId: string;
  /** The school year. */
  schoolYear: number;
  /** The start date of the assessment period. */
  startDate: DateOnly;
  /** Beginning of Year window. */
  boyWindow: number;
  /** Middle of Year window. */
  moyWindow: number;
  /** End of Year window. */
  eoyWindow: number;
  /** A list of identified periods. */
  periods?: IdentifiedPeriod[];
  /** The current period. */
  currentPeriod?: IdentifiedPeriod;
}

// --- Input Types ---

export type AcademicBreakPeriodInput = {
	/** Name of the break period. */
	name: string;
	/** Type of the break period. */
	breakType: BreakType;
	startDate: DateOnly;
	endDate: DateOnly;
};

export type SchoolYearCalendarInput = {
	name?: string;
	startDate: DateOnly;
	endDate: DateOnly;
	districtId?: string;
	schoolId?: string;
	schoolYear: number;
	academicBreakPeriods?: AcademicBreakPeriodInput[];
};

/** Input for an identified period. */
export type IdentifiedPeriodInput = {
	/** Unique identifier for the period. */
	periodIdentifier: number;
	/** Start date of the period. */
	startDate: DateOnly;
	/** End date of the period. */
	endDate: DateOnly;
};

/** Input for creating an assessment calendar. */
export type AssessmentCalendarInput = {
	districtId?: string;
	schoolId?: string;
	schoolYear: number;
	/** The start date of the assessment calendar. */
	startDate?: DateOnly;
	/** Beginning of year window. */
	boyWindow?: number;
	/** Middle of year window. */
	moyWindow?: number;
	/** End of year window. */
	eoyWindow?: number;
	/** A list of identified periods. */
	periods?: IdentifiedPeriodInput[];
};

// --- Root Query/Mutation Response Wrappers ---

export type PingResponse = {
	ping: string;
};

export type SchoolYearCalendarResponse = {
	schoolYearCalendar: SchoolYearCalendar;
};

export type AssessmentCalendarResponse = {
	assessmentCalendar: AssessmentCalendar;
};

export type GetCurrentPeriodResponse = {
	getCurrentPeriod: IdentifiedPeriod;
};

export type GetPeriodForDateResponse = {
	getPeriodForDate: IdentifiedPeriod;
};

export type GetPeriodResponse = {
	getPeriod: IdentifiedPeriod;
};

export type GetPeriodByIdResponse = {
	getPeriodById: IdentifiedPeriod;
};

export type UpsertSchoolYearCalendarResponse = {
	upsertSchoolYearCalendar: SchoolYearCalendar;
};

export type UpsertAssessmentCalendarResponse = {
	upsertAssessmentCalendar: AssessmentCalendar;
};

export type CurrentSchoolYearCalendarResponse = {
	getCurrentSchoolYearCalendar: SchoolYearCalendar;
};

// --- Query Functions (Copied from calendarQueries.ts) ---

export async function pingCalendarApi(): Promise<PingResponse | undefined> {
	return calendarGraphqlRequest<PingResponse>(GQL_PING);
}

export async function fetchSchoolYearCalendar(variables: {
	districtId?: string;
	schoolId?: string;
	schoolYear: number;
}): Promise<SchoolYearCalendarResponse | undefined> {
	return calendarGraphqlRequest<SchoolYearCalendarResponse>(
		GQL_SCHOOL_YEAR_CALENDAR,
		variables
	);
}

export async function fetchAssessmentCalendar(variables: {
	districtId?: string;
	schoolId?: string;
	schoolYear: number;
}): Promise<AssessmentCalendarResponse | undefined> {
	return calendarGraphqlRequest<AssessmentCalendarResponse>(
		GQL_ASSESSMENT_CALENDAR,
		variables
	);
}

export async function fetchCurrentPeriod(variables: {
	schoolId: string;
}): Promise<GetCurrentPeriodResponse | undefined> {
	return calendarGraphqlRequest<GetCurrentPeriodResponse>(
		GQL_GET_CURRENT_PERIOD,
		variables
	);
}

export async function fetchPeriodForDate(variables: {
	schoolId: string;
	date: DateOnly;
}): Promise<GetPeriodForDateResponse | undefined> {
	return calendarGraphqlRequest<GetPeriodForDateResponse>(
		GQL_GET_PERIOD_FOR_DATE,
		variables
	);
}

export async function fetchPeriod(variables: {
	userId: Long;
	date: DateOnly;
}): Promise<GetPeriodResponse | undefined> {
	return calendarGraphqlRequest<GetPeriodResponse>(GQL_GET_PERIOD, variables);
}

export async function fetchPeriodById(variables: {
	districtId?: string;
	schoolId?: string;
	periodId: number;
	schoolYear?: number;
}): Promise<GetPeriodByIdResponse | undefined> {
	return calendarGraphqlRequest<GetPeriodByIdResponse>(
		GQL_GET_PERIOD_BY_ID,
		variables
	);
}

// --- Mutation Functions (Copied from calendarMutations.ts) ---

export async function upsertSchoolYearCalendar(variables: {
	calendar: SchoolYearCalendarInput;
}): Promise<UpsertSchoolYearCalendarResponse | undefined> {
	return calendarGraphqlRequest<UpsertSchoolYearCalendarResponse>(
		GQL_UPSERT_SCHOOL_YEAR_CALENDAR,
		variables
	);
}

/**
 *
 * @param {*} params districtId, schoolId
 * @returns
 */
export async function getCurrentSchoolYearCalendar(variables: {
	districtId?: string;
	schoolId?: string;
}): Promise<CurrentSchoolYearCalendarResponse | undefined> {
	const resp = await calendarGraphqlRequest<CurrentSchoolYearCalendarResponse>(
		GQL_GET_CURRENT_SCHOOL_YEAR_CALENDAR,
		variables
	);
	return resp;
}
